/**
 * Saved documents — the runs the pipeline leaves behind.
 *
 * Backed by `/api/v1/document-extractions` (collection: document_extraction).
 * A run becomes a saved document through `save` — the pipeline stores nothing
 * on its own, so it is the operator pressing Save that creates the row. The
 * rest covers everything afterwards: listing them, opening one, correcting a
 * field, removing one.
 *
 * `fileUrl` on a fetched document is a short-lived signed link, minted by the
 * backend per read. Don't cache it — refetch the document instead.
 */
import { api } from './api';
import type { PaginatedResponse } from '../types';
import type {
  DocumentLanguage,
  DocumentProcessingResult,
  SavedDocument,
} from '../types/document-intelligence.types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface SavedDocumentQueryParams {
  search?: string;
  category?: string;
  status?: 'COMPLETED' | 'FAILED' | 'PROCESSING';
  page?: number;
  page_size?: number;
  /** createdAt | fileName | category | status */
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  app_id?: string;
  /** Only documents processed as this language; omit for every language. */
  language?: DocumentLanguage;
}

/** One corrected field. Send only what changed. */
export interface FieldCorrection {
  fieldName: string;
  value: string | number | null;
  /** Send only to change it; omitted, the stored translation is kept. */
  translatedValue?: string | number | null;
}

const BASE = '/api/v1/document-extractions';

export const documentExtractionsService = {
  /** Keep a processed document.
   *
   *  The file goes up a second time because the backend held on to nothing
   *  after processing, and `result` is sent from here so any correction the
   *  operator made on screen is part of what gets stored.
   */
  save: async (file: File, result: DocumentProcessingResult): Promise<SavedDocument> => {
    const form = new FormData();
    form.append('file', file);
    form.append('result', JSON.stringify(result));
    const response = await api.post<ApiResponse<SavedDocument>>(`${BASE}/save`, form,
      // Overrides the instance's application/json default, which would make
      // axios serialise the FormData as JSON — the server then sees neither
      // part and answers "file: Field required, result: Field required".
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data.data;
  },

  list: async (params: SavedDocumentQueryParams = {}): Promise<PaginatedResponse<SavedDocument>> => {
    const response = await api.get<PaginatedResponse<SavedDocument>>(`${BASE}/list`, { params });
    return response.data;
  },

  /** One document, with a fresh link to the file. `includeText` also returns
   *  the full extracted text, which the list never carries. */
  getById: async (id: string, includeText = false): Promise<SavedDocument> => {
    const response = await api.get<ApiResponse<SavedDocument>>(`${BASE}/get/${id}`, {
      params: includeText ? { include_text: true } : undefined,
    });
    return response.data.data;
  },

  /** Rename a document.
   *
   *  Send the name WITHOUT its extension — the server keeps the stored one, so
   *  a renamed document still downloads as the file it is.
   */
  rename: async (id: string, fileName: string): Promise<SavedDocument> => {
    const response = await api.patch<ApiResponse<SavedDocument>>(
      `${BASE}/rename/${id}`, { fileName },
    );
    return response.data.data;
  },

  /** Apply corrections. The AI's own answer is kept on each corrected field. */
  updateFields: async (id: string, fields: FieldCorrection[]): Promise<SavedDocument> => {
    const response = await api.patch<ApiResponse<SavedDocument>>(
      `${BASE}/update-fields/${id}`, { fields },
    );
    return response.data.data;
  },

  /** Soft delete — the stored file is left in place. */
  delete: async (id: string): Promise<null> => {
    const response = await api.delete<ApiResponse<null>>(`${BASE}/delete/${id}`);
    return response.data.data;
  },
};
